import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { of, Subject } from 'rxjs';
import { catchError, take, takeUntil, timeout } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { ElectionStatusEnum } from 'src/app/enums/ElectionStatusEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { IReportCandidateLeadingInDistrict } from 'src/app/interfaces/IReportCandidateLeadingInDistrict';
import { IReportFirstPreference } from 'src/app/interfaces/IReportFirstPreference';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { ITableHeader } from 'src/app/interfaces/ITableHeader';
import { ElectionService } from 'src/app/services/election.service';


@Component({
  selector: 'leading-candidate-chart',
  templateUrl: './leading-candidate-chart.component.html',
  styleUrls: ['./leading-candidate-chart.component.scss']
})
export class LeadingCandidateChartComponent implements AfterViewInit, OnDestroy {

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  @Input() isFinal: boolean = false;

  public title = '';
  public candidateTitle = '';
  public candidates: IReportCandidateLeadingInDistrict[] = [];
  public dvWid = '10px';
  public dataTable: IReportCandidateLeadingInDistrict[] = [];
  public dataSource = new MatTableDataSource<IReportCandidateLeadingInDistrict>();
  public columns: ITableHeader[] = [];
  public displayedColumns: string[] = [];
  public dataIsLoaded = true;

  constructor(private electionService: ElectionService) {

    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(dataLoaded => {
      if (this.isDataLoaded(dataLoaded.electionDate)) {
        this.loadChart(this.electionService.selectedPage);
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(page => {
      this.loadChart(page);
    });
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }


  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart(this.electionService.selectedPage);
    }
  }


  private loadChart(page: ISelectPage): void {
    this.columns = [];
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');

    // Ensure isFinal reflects the HAChange status so the header updates correctly
    this.isFinal = election?.haChange?.electionStatus === ElectionStatusEnum.final;

    if (page.pageType === PageTypeEnum.CountHAStateWide) {
      this.candidateTitle = this.isFinal ? 'Elected Candidate' : 'Leading Candidate';
      this.candidates = election?.electionCount?.districts.filter(x => x.leadingCandidate !== null).map(district => {
        return {
          district: district.districtName,
          candidateName: `${district.leadingCandidate?.wasPreviousMember ? '* ' : ''}${district.leadingCandidate?.candidateName ?? ''}`,
          wasPreviousMember: district.leadingCandidate?.wasPreviousMember ?? false,
          partyName: district.leadingCandidate?.partyId === 'IND'
            ? 'Independent'
            : `${district.leadingCandidate?.partyName ?? ''} (${district.leadingCandidate?.partyId ?? ''})`,
          colourCode: district.leadingCandidate?.colourCode ?? '',
          distributionVotePercent: district.leadingCandidate?.twoCPVotePercent ?? 0,
          divWid: '',
          divStyle: `width: ${Math.round(district.leadingCandidate?.twoCPVotePercent ?? 0)}px; fill: ${district.leadingCandidate?.colourCode}; stroke-width: 3; stroke: ${district.leadingCandidate?.colourCode}`
        };
      }) ?? [];
    }
    this.columns.push({
      column: 'district',
      label: 'District',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 140,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'colourCode',
      label: '',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 20,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: true,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'candidateName',
      label: this.isFinal ? 'Elected Candidate' : 'Leading Candidate',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 200,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'partyName',
      label: 'Affiliation',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 120,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'distributionVotePercent',
      label: '% of Two-Candidate Preferred votes',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: true,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });

    this.columns.push({
      column: 'divStyle',
      label: '',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: true,
      isColour: false,
      isColourBar: true,
      colourCode: null
    });

    this.dataTable = this.candidates;
    this.displayedColumns = this.columns.map(c => c.column);
    if (this.dataTable?.length > 0) {
      this.dataSource = new MatTableDataSource<IReportCandidateLeadingInDistrict>(this.dataTable);
    }
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

}
